import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import { ChevronDown } from 'lucide-react';

import {
  LICENSE_NAMES,
  type LicenseGroup,
  OSS_ENTRIES,
  groupByLicense,
} from './licenses';
import { LICENSE_TEXTS } from './licenseTexts';
import { Card, PAGE_HEADER_STICKY_TOP, PageHeader } from '../../components';

/**
 * 오픈소스 라이선스(`/more/docs/licenses`) — 라이선스별 묶음 카드. 행은 패키지 이름과
 * 저작권자, 카드 끝의 펼침이 그 라이선스 전문이다. MIT·ISC·Apache 는 "저작권 고지와 허가
 * 문구를 함께 싣는다"가 조건이라 이름만 늘어놓은 목록으로는 모자란다 — 전문은 묶음마다
 * 한 번, 저작권자는 행마다 싣는다.
 *
 * 약관·방침(`features/policy`)처럼 자기 화면이다: 본문이 번역 문장이 아니라 데이터(`licenses.ts`)라서.
 */

/** 묶음 앵커 — 위 점프 칩이 이 id 로 스크롤한다. SPDX id 는 id 에 써도 되는 글자뿐이다. */
const sectionId = (group: LicenseGroup): string => `license-${group.license}`;

/** 점프 칩의 짧은 이름 — 한 줄에 넷이 서야 한다. */
const SHORT_NAMES: Record<LicenseGroup['license'], string> = {
  MIT: 'MIT',
  'Apache-2.0': 'Apache 2.0',
  ISC: 'ISC',
  'OFL-1.1': 'OFL 1.1',
};

const LicenseSection = ({ group }: { group: LicenseGroup }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const headingId = useId();
  const textId = useId();
  const name = LICENSE_NAMES[group.license];

  return (
    <section
      id={sectionId(group)}
      aria-labelledby={headingId}
      // 고정 상단 바 밑으로 파고들지 않게 — 점프 칩이 이 섹션으로 스크롤할 때.
      style={{ scrollMarginTop: `calc(${PAGE_HEADER_STICKY_TOP} + 12px)` }}
      className="flex flex-col gap-3"
    >
      <div className="flex items-baseline justify-between px-1">
        <h2
          id={headingId}
          className="text-[17px] font-semibold tracking-[-0.1px] text-bk-900"
        >
          {name}
        </h2>
        <span className="text-[13px] tabular-nums text-bk-500">
          {t('web.more.licenses.count', { count: group.entries.length })}
        </span>
      </div>

      <Card className="overflow-hidden">
        <ul className="divide-y divide-bk-50">
          {group.entries.map((entry) => (
            <li key={entry.name} className="px-5 py-3">
              <p className="text-[15px] font-medium text-bk-900">
                {entry.name}
              </p>
              <p className="mt-0.5 text-[13px] leading-snug text-bk-500">
                © {entry.copyright}
              </p>
            </li>
          ))}
        </ul>

        <button
          type="button"
          aria-expanded={open}
          aria-controls={textId}
          onClick={() => setOpen((v) => !v)}
          className="flex h-[52px] w-full items-center justify-between border-t border-bk-50 px-5 text-sm font-medium text-bk-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          {t(open ? 'web.more.licenses.hide' : 'web.more.licenses.show', {
            name,
          })}
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'h-5 w-5 text-bk-400 transition-transform motion-reduce:transition-none',
              open && 'rotate-180',
            )}
          />
        </button>

        {/* 원문은 영어다 — 법적 문구라 번역하지 않는다. `lang` 이 스크린리더 발음을 맞춘다.
            `hidden` 은 바깥 div 가 든다 — 같은 요소에 `flex` 를 주면 클래스가 `[hidden]` 의
            `display: none` 을 이겨 처음부터 펼쳐진다. */}
        <div id={textId} hidden={!open} lang="en">
          <div className="flex flex-col gap-3 px-5 pb-6 text-[13px] leading-relaxed text-bk-600">
            {LICENSE_TEXTS[group.license].map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
        </div>
      </Card>
    </section>
  );
};

export const LicensesPage = () => {
  const { t } = useTranslation();
  const groups = groupByLicense(OSS_ENTRIES);

  return (
    <div className="flex min-h-dvh flex-col bg-background pb-10">
      <PageHeader title={t('web.more.items.licenses')} />
      <div className="flex flex-col gap-8 px-[18px] pt-2">
        <div className="flex flex-col gap-4">
          <p className="whitespace-pre-line px-1 text-sm leading-relaxed text-bk-600">
            {t('web.more.licenses.intro')}
          </p>
          {/* MIT 묶음이 스무 줄을 넘어 아래 묶음은 한참 내려가야 보인다 — 위에서 바로 건너뛴다. */}
          <nav
            aria-label={t('web.more.licenses.jump')}
            className="flex flex-wrap gap-2"
          >
            {groups.map((group) => (
              <button
                key={group.license}
                type="button"
                onClick={() =>
                  document
                    .getElementById(sectionId(group))
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
                className="inline-flex h-9 items-center gap-1.5 rounded-full bg-white px-3.5 text-[13px] font-medium text-bk-800 shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {SHORT_NAMES[group.license]}
                <span className="tabular-nums text-bk-400">
                  {group.entries.length}
                </span>
              </button>
            ))}
          </nav>
        </div>
        {groups.map((group) => (
          <LicenseSection key={group.license} group={group} />
        ))}
      </div>
    </div>
  );
};
